/**
 * Async mutex for coordinating concurrent operations.
 *
 * Use this when multiple async code paths may modify the same resource
 * and order of operations matters for correctness.
 *
 * @example
 * // Global mutex - serializes ALL calls
 * class DatabaseWrapper {
 *   private writeMutex = new Mutex();
 *
 *   async save(doc: Doc): Promise<void> {
 *     return this.writeMutex.exclusiveCall(() => this.saveInternal(doc));
 *   }
 * }
 *
 * @example
 * // Per-resource mutex - allows parallel access to different resources
 * class CacheManager {
 *   private cacheLocks = new Map<string, Mutex>();
 *
 *   async updateCache(key: string): Promise<void> {
 *     const lock = this.cacheLocks.get(key) ?? new Mutex();
 *     this.cacheLocks.set(key, lock);
 *     return lock.exclusiveCall(() => this.updateInternal(key));
 *   }
 * }
 *
 * @example
 * // Debug mode - logs acquire/release
 * private writeMutex = new Mutex('DatabaseWrapper.write');
 */
export class Mutex {
  private promise: Promise<void> | null = null;
  private resolve: (() => void) | null = null;

  private lockBuffer: ArrayBuffer | SharedArrayBuffer;
  private lockCounter: Int32Array;
  private debugName?: string;

  constructor(debugName?: string) {
    this.debugName = debugName;
    if (typeof SharedArrayBuffer === 'undefined') {
      this.lockBuffer = new ArrayBuffer(4);
      this.lockCounter = new Int32Array(this.lockBuffer);
    } else {
      this.lockBuffer = new SharedArrayBuffer(4);
      this.lockCounter = new Int32Array(this.lockBuffer);
    }
  }

  private async acquire(): Promise<void> {
    if (this.debugName) {
      console.debug(`[Mutex:${this.debugName}] acquire requested, waiters: ${this.getCount()}`);
    }
    Atomics.add(this.lockCounter, 0, 1);
    while (this.promise != null) {
      await this.promise;
    }
    this.promise = new Promise<void>((res) => (this.resolve = res));
    if (this.debugName) {
      console.debug(`[Mutex:${this.debugName}] acquired`);
    }
  }

  private release(): void {
    if (this.resolve) {
      this.resolve();
      this.promise = null;
      this.resolve = null;
    }
    Atomics.sub(this.lockCounter, 0, 1);
    if (this.debugName) {
      console.debug(`[Mutex:${this.debugName}] released, waiters: ${this.getCount()}`);
    }
  }

  public getCount(): number {
    return Atomics.load(this.lockCounter, 0);
  }

  // Safe way to ensure that the lock is released even if the function throws.
  public async exclusiveCall<T>(fn: () => Promise<T>): Promise<T> {
    await this.acquire();
    try {
      return await fn();
    } finally {
      this.release();
    }
  }
}
